import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  peekMotorMonitoringService,
  type MotorHistoryPoint,
} from "../services/MotorMonitoringService";
import SeriesChart, {
  type SeriesDef,
  type AxisDef,
} from "../dashboard/panels/SeriesChart";
import { DashboardSyncProvider } from "../dashboard/DashboardSyncContext";
import type { SeriesPoint } from "../dashboard";
import TreeView, { type TreeNode } from "./TreeView";
import { cx } from "@digitalisation/enlixy-comp";

/**
 * One chart per servo signal. Every axis on the machine is drawn as its own
 * series inside that chart, so a single glance compares all axes for the same
 * measurement (the Grafana-style "Temperature Servomotoren" layout).
 */
const HIST_SIGNALS: {
  field: keyof MotorHistoryPoint;
  title: string;
  unit: string;
}[] = [
  { field: "tempAmplifier", title: "Temperature Servo Amplifier", unit: "°C" },
  { field: "tempMotor", title: "Temperature Servo Motor", unit: "°C" },
  { field: "torqueRms", title: "Torque RMS", unit: "Nm" },
  { field: "torqueDc", title: "Torque DC", unit: "Nm" },
  { field: "lagDistanceRms", title: "Lag Distance RMS", unit: "°/mm" },
  { field: "lagDistanceDc", title: "Lag Distance DC", unit: "°/mm" },
  { field: "velocity", title: "Velocity", unit: "rpm" },
];

/**
 * Distinct colour per axis via the golden-angle hue rotation — a machine can
 * have 20+ axes, far more than any fixed palette covers, and neighbouring
 * indices stay visually far apart.
 */
const axisColor = (i: number): string => `hsl(${(i * 137.508) % 360} 70% 50%)`;

/** Compact axis label: "P0655_F_ZFSB_fbCigFormation_MA03" → "fbCigFormation MA03". */
function shortAxisLabel(axisId: string): string {
  const axis = axisId.match(/MA\d+/)?.[0] ?? "";
  const parts = axisId.replace(/_MA\d+$/, "").split("_");
  const component = parts[parts.length - 1] ?? axisId;
  return axis ? `${component} ${axis}` : component;
}

interface Props {
  machineBK?: string;
  /** ISO-8601 range start. Undefined (live) falls back to a recent window. */
  startTime?: string;
  isDark: boolean;
  /** Axis tree (same as the Overview tab) for selecting a single axis. */
  treeNodes: TreeNode[];
  /** Currently selected axis id, or null when none is selected. */
  selectedAxisId: string | null;
  /** Select (or clear) an axis. */
  onSelectAxis: (axisId: string | null) => void;
}

/** Window loaded when no explicit range is selected (live mode). */
const DEFAULT_WINDOW_MS = 3 * 60 * 60 * 1000;

const ServoHistoricalValues: React.FC<Props> = ({
  machineBK,
  startTime,
  isDark,
  treeNodes,
  selectedAxisId,
  onSelectAxis,
}) => {
  const [history, setHistory] = useState<Map<string, MotorHistoryPoint[]>>(
    new Map(),
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Ignore results from a superseded request (range switched mid-flight).
  const reqIdRef = useRef(0);

  useEffect(() => {
    if (!machineBK) return;
    const service = peekMotorMonitoringService(machineBK);
    if (!service) return;

    const from =
      startTime ?? new Date(Date.now() - DEFAULT_WINDOW_MS).toISOString();
    const reqId = ++reqIdRef.current;

    setLoading(true);
    setError(null);
    service
      .loadAllAxesHistory(from)
      .then((map) => {
        if (reqId !== reqIdRef.current) return;
        setHistory(map);
        if (map.size === 0) setError("No historical servo data in this range.");
      })
      .catch(() => {
        if (reqId !== reqIdRef.current) return;
        setError("Could not load servo history.");
      })
      .finally(() => {
        if (reqId === reqIdRef.current) setLoading(false);
      });
  }, [machineBK, startTime]);

  /**
   * "Full Resolution" for the visible zoom window — same action the motor
   * charts expose, but fetched for every axis at once. Detail points win over
   * the aggregated ones they overlap.
   */
  const handleDetail = useCallback(
    async (startMs: number, endMs: number) => {
      if (!machineBK) return;
      const service = peekMotorMonitoringService(machineBK);
      if (!service) return;

      const detail = await service.loadAllAxesDetail(
        new Date(startMs).toISOString(),
        new Date(endMs).toISOString(),
      );
      if (detail.size === 0) return;

      setHistory((prev) => {
        const merged = new Map(prev);
        detail.forEach((points, axisId) => {
          const existing = merged.get(axisId) ?? [];
          // Drop aggregated points inside the detail window, then splice the
          // full-resolution ones in and re-sort.
          const outside = existing.filter(
            (p) => p.timestamp < startMs || p.timestamp > endMs,
          );
          merged.set(
            axisId,
            [...outside, ...points].sort((a, b) => a.timestamp - b.timestamp),
          );
        });
        return merged;
      });
    },
    [machineBK],
  );

  /**
   * Per signal: a single series for the SELECTED axis only. Signals with no
   * data for that axis are dropped so we don't show empty charts.
   */
  const charts = useMemo(() => {
    if (!selectedAxisId) return [];
    const points = history.get(selectedAxisId) ?? [];
    return HIST_SIGNALS.map((signal) => {
      const pts: SeriesPoint[] = [];
      for (const p of points) {
        const v = (p as unknown as Record<string, unknown>)[
          signal.field as string
        ];
        if (typeof v === "number") pts.push({ t: p.timestamp, v });
      }
      if (pts.length === 0) return null;
      const data = new Map<string, SeriesPoint[]>([[selectedAxisId, pts]]);
      const seriesDefs: SeriesDef[] = [
        {
          key: selectedAxisId,
          name: shortAxisLabel(selectedAxisId),
          color: axisColor(0),
          unit: signal.unit,
          axis: 0,
          showMarkers: true,
        },
      ];
      const yAxes: AxisDef[] = [{ title: signal.unit, opposite: false }];
      return { ...signal, data, seriesDefs, yAxes };
    }).filter((c): c is NonNullable<typeof c> => c !== null);
  }, [selectedAxisId, history]);

  const cardCls = cx(
    "rounded-xl border",
    isDark ? "bg-gray-800 border-gray-700" : "bg-white border-gray-200",
  );

  if (!machineBK) {
    return (
      <div
        className={cx(
          "py-14 text-center text-sm",
          isDark ? "text-gray-400" : "text-gray-500",
        )}
      >
        No machine selected
      </div>
    );
  }

  // Right-hand content depends on selection / load state; the axis tree on the
  // left stays visible throughout so the layout matches the Overview tab.
  const renderRight = () => {
    if (!selectedAxisId) {
      return (
        <div
          className={cx(
            cardCls,
            "p-4 flex flex-col items-center justify-center py-14 text-center",
            isDark ? "text-gray-500" : "text-gray-400",
          )}
        >
          <span className="text-3xl mb-2">👈</span>
          <p className="text-sm">Select a servo axis to view its history</p>
        </div>
      );
    }
    if (loading) {
      return (
        <div className="flex flex-col items-center justify-center py-16 gap-3">
          <div className="animate-spin rounded-full h-9 w-9 border-b-2 border-blue-600" />
          <span
            className={cx(
              "text-sm",
              isDark ? "text-gray-400" : "text-gray-500",
            )}
          >
            Loading servo history…
          </span>
        </div>
      );
    }
    if (charts.length === 0) {
      return (
        <div
          className={cx(
            cardCls,
            "p-4 py-14 text-center",
            isDark ? "text-gray-400" : "text-gray-500",
          )}
        >
          <span className="text-3xl block mb-2">🗂️</span>
          <p className="text-sm">
            {error ?? "No historical servo data for this axis."}
          </p>
          {!startTime && (
            <p className="text-xs mt-1 opacity-75">
              Pick a date range above to load more history.
            </p>
          )}
        </div>
      );
    }
    return (
      <div className="space-y-4">
        <div
          className={cx("text-xs", isDark ? "text-gray-400" : "text-gray-500")}
        >
          {shortAxisLabel(selectedAxisId)} · {charts.length} signals · zoom and
          hover are linked across all charts · zoom in, then use “Full
          Resolution” for un-aggregated data
        </div>

        {/* One provider for all charts → shared zoom/hover crosshair. */}
        <DashboardSyncProvider>
          <div className="space-y-4">
            {charts.map((c) => (
              <div key={c.field as string} className={cx(cardCls, "p-3")}>
                <SeriesChart
                  title={c.title}
                  seriesDefs={c.seriesDefs}
                  yAxes={c.yAxes}
                  data={c.data}
                  height={280}
                  canDetail
                  onDetail={handleDetail}
                  commentType={`servo_hist_${c.field as string}`}
                  machineBK={machineBK}
                />
              </div>
            ))}
          </div>
        </DashboardSyncProvider>
      </div>
    );
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-4 gap-4 items-start">
      {/* Axis tree (same as the Overview tab) */}
      <div className={cx(cardCls, "lg:col-span-1 p-4 flex flex-col")}>
        <h3
          className={cx(
            "text-sm font-bold mb-3",
            isDark ? "text-gray-200" : "text-gray-700",
          )}
        >
          All axes
        </h3>
        <div className="flex-1 min-h-0 overflow-y-auto pr-1">
          <TreeView
            nodes={treeNodes}
            showControls
            selectedId={selectedAxisId ?? undefined}
            onSelect={(node) => {
              if (!node.children || node.children.length === 0)
                onSelectAxis(node.id);
            }}
          />
        </div>
      </div>

      {/* History charts for the selected axis */}
      <div className="lg:col-span-3">{renderRight()}</div>
    </div>
  );
};

export default ServoHistoricalValues;
